/**
 * Sparrow brand mark — v2.
 *
 * Two-part: a small geometric "rising bars" icon (three vertical
 * bars increasing in height, with the tallest in the mint accent)
 * + the wordmark "Sparrow" set in Space Grotesk.
 *
 * The bird is gone. The bars read as "data, going up" and work at
 * 16px (favicon) up to 200px (hero overlay). The accent dot after
 * the wordmark carries the brand colour even when the icon is
 * cropped out.
 */

type Size = "sm" | "md" | "lg" | "xl"

const SIZES: Record<Size, { svg: number; word: string; gap: string }> = {
  sm: { svg: 16, word: "text-base", gap: "gap-1.5" },
  md: { svg: 22, word: "text-xl",   gap: "gap-2"   },
  lg: { svg: 32, word: "text-3xl",  gap: "gap-2.5" },
  xl: { svg: 52, word: "text-5xl",  gap: "gap-3"   },
}

interface Props {
  size?: Size
  /** Hide the wordmark — for the favicon, footer chip, etc. */
  iconOnly?: boolean
  className?: string
}

export function Sparrowmark({ size = "md", iconOnly = false, className = "" }: Props) {
  const s = SIZES[size]
  return (
    <span className={`inline-flex items-center ${s.gap} ${className}`}>
      <svg
        width={s.svg}
        height={s.svg}
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden
        className="shrink-0"
      >
        {/* Three rising bars — primary, primary, accent. Heights
            chosen so the silhouette reads as "ascending data" at
            any size. Rounded tops keep the mark friendly. */}
        <rect x="3"  y="20" width="6" height="9"  rx="1.5" fill="currentColor" opacity={0.85} />
        <rect x="13" y="14" width="6" height="15" rx="1.5" fill="currentColor" />
        <rect x="23" y="6"  width="6" height="23" rx="1.5" fill="oklch(0.78 0.18 165)" />
      </svg>
      {!iconOnly && (
        <span
          className={`font-display font-semibold tracking-tight leading-none ${s.word}`}
          style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
        >
          Sparrow
          <span
            aria-hidden
            className="inline-block ml-0.5 align-baseline"
            style={{ color: "oklch(0.78 0.18 165)" }}
          >
            .
          </span>
        </span>
      )}
    </span>
  )
}
